import { Ban, MailX } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

import CONSTANTS from "constants/Constants";
import { hasInvalidEmailDomain } from "helpers/contacts";

const { MX_DOMAIN } = CONSTANTS.EMAIL_DISABLED_REASONS;

const MX_DOMAIN_SYSTEM_MESSAGE =
  "Disabled by system — domain has no mail server";

const MX_DOMAIN_USER_MESSAGE =
  "Domain has no mail server. Disabled separately — see Activity for details";

const MX_DOMAIN_ENABLED_MESSAGE =
  "Domain has no mail server — this address will be disabled at the next domain check";

const DISABLED_MESSAGE = "Disabled — see Activity for details";

// Only disabled_reason says who switched the address off; `enabled` alone would
// credit the system for a manual change that happens to sit on a bad domain.
const domainMessageFor = ({ enabled, disabled_reason }) => {
  if (disabled_reason === MX_DOMAIN) {
    return MX_DOMAIN_SYSTEM_MESSAGE;
  }

  return enabled === false ? MX_DOMAIN_USER_MESSAGE : MX_DOMAIN_ENABLED_MESSAGE;
};

// The status icon that names why an address is flagged, or null when it is a
// healthy, enabled address. An invalid domain always wins over a plain disable:
// on a bad domain the badge (inline placement) or bare MailX (destructive group)
// carries the domain reason, while a user-disabled address on a good domain
// shows a ban icon. Both hang the reason off the same tooltip.
const statusFor = (emailAddress, destructive) => {
  if (hasInvalidEmailDomain(emailAddress)) {
    const message = domainMessageFor(emailAddress);
    return {
      message,
      icon: destructive ? (
        <MailX className="tw:size-4" />
      ) : (
        <Badge variant="warning" size="sm">
          <MailX data-icon="inline-start" />
          INVALID DOMAIN
        </Badge>
      ),
    };
  }

  if (emailAddress.enabled === false) {
    return {
      message: DISABLED_MESSAGE,
      icon: <Ban className="tw:size-4 tw:text-muted-foreground" />,
    };
  }

  return null;
};

/**
 * Renders one of a record's email addresses for a read-only sidebar.
 *
 * Every address goes through one layout: the address as a shrink-and-break span
 * so a long address wraps instead of forcing the rail to scroll, followed by an
 * optional status icon and its tooltip. A disabled address is struck through; a
 * bad-domain address is called out on its own, because a user who cannot tell it
 * from a colleague's manual change will simply switch it back on and have it
 * disabled again at the next validation cycle, so the flag outlives the
 * re-enable.
 *
 * `destructive` is for the panel's dedicated "Invalid domain emails" group: the
 * address renders in red with a bare MailX icon, dropping the badge that the
 * inline (dialer) placement still needs to name the condition.
 */
function ContactEmailDetails({ emailAddress, destructive = false }) {
  const { email, is_primary, enabled } = emailAddress;
  const label = is_primary ? `${email}*` : email;
  const disabled = enabled === false;
  const status = statusFor(emailAddress, destructive);

  return (
    <span
      className={cn(
        "tw:flex tw:min-w-0 tw:items-center tw:gap-1.5",
        destructive && "tw:text-destructive"
      )}
    >
      <span
        className={cn(
          "tw:min-w-0 tw:break-all",
          disabled && "tw:line-through",
          disabled && !destructive && "tw:text-muted-foreground"
        )}
      >
        {label}
      </span>
      {status && (
        <Tooltip>
          <TooltipTrigger
            aria-label={status.message}
            className="tw:shrink-0 tw:cursor-help tw:border-0 tw:bg-transparent tw:p-0"
          >
            {status.icon}
          </TooltipTrigger>
          <TooltipContent>{status.message}</TooltipContent>
        </Tooltip>
      )}
    </span>
  );
}

export default ContactEmailDetails;
